import {
    createEffect,
    getOwner,
    onCleanup,
    runWithOwner,
} from 'solid-js'

import sessionStore from '../../stores/session-store'
import userStore from '../../stores/user-store'
import legalAcceptanceStore from '../../stores/legal-acceptance-store.js'
import SessionService from '../services/session-service'
import {
    LODGR_EXT_AUTH_PUSH,
    LODGR_EXT_AUTH_REVOKED,
} from '../utils/extension-installation.js'
import UserService from '../services/user-service'
import {
    captureError,
    clearSentryUser,
} from '../../utilities/use-sentry.js'
import { reset as posthogReset } from '../../utilities/use-posthog.js'
import { buildSignInRedirectPath } from '../utils/sign-in-redirect.js'

// TENANT-5036: `ProfileService`/`PropertyService`/`InboxService` are only used
// inside authenticated route guards below (`useVerificationGuard`,
// `checkPropertyOwnership`) that never run for anonymous marketing-page
// visits. This module is imported into the eager main boot chunk (it backs
// `useAuthSync`, which every page needs), so statically importing these
// services here shipped their code to every visitor regardless of route.
// Dynamic-importing them keeps guard behavior identical for authenticated
// users while keeping the code out of the main bundle for everyone else.
const loadProfileService = () => import('../services/profile-service').then(m => m.default)
const loadPropertyService = () => import('../services/property-service').then(m => m.default)
const loadInboxService = () => import('../services/inbox-service').then(m => m.default)

// TODO: add routes as we go here or full refactor
export const ROUTES = {
    COMPLETE_PROFILE: '/complete-profile',
    REFER: '/refer',
    REFERRAL_CENTER: '/referral-center',
    STAY_IN_THE_LOOP: '/stay-in-the-loop',
}

export const useAuthSync = async () => {
    // Captured BEFORE the first await. Solid tracks the reactive owner
    // synchronously, so once this function suspends the owner is gone and a
    // bare `onCleanup` below would be registered against nothing — Solid warns
    // "cleanups created outside a `createRoot` or `render` will never be run"
    // and the two window listeners at the foot of this function would outlive
    // every caller. `runWithOwner` puts the cleanup back on the owner this was
    // called under.
    const owner = getOwner()

    await SessionService.checkAuthentication()

    const handleStorageChange = async (event) => {
        if (event.key === 'session_access_token') {
            // Other tabs may have logged in, logged out, or refreshed their token.
            // In every case we just re-sync the auth signal from the stored token:
            //   - login elsewhere: token appears, signal flips true, this tab is now authed
            //     and stays on its current page.
            //   - logout elsewhere: token cleared, signal flips false, guarded pages bounce
            //     via useAuthGuard's createEffect — but we mark this as a cross-tab logout
            //     first so the effect routes to `/` cleanly instead of opening the login
            //     modal at `/?login=true&from=...`.
            //   - token swap (same or different user): signal stays true, no redirect.
            // We intentionally do not force a navigation here.
            const isLogoutFromAnotherTab = Boolean(event.oldValue) && !event.newValue

            if (isLogoutFromAnotherTab) {
                SessionService.markCrossTabLogout()
                // The originating tab's `SessionService.delete`/`logout` already
                // called `posthog.reset()` for itself, but that is tab-local. Reset
                // here too so this tab's PostHog session does not stay identified
                // as the previous user — without this, the next identify() in this
                // tab (e.g. login as a different account) is silently dropped by
                // posthog-js until reset is called. Clear the Sentry user for the
                // same reason: a cross-tab logout must not leave the prior user's
                // id+email attached to this tab's later error events / replays.
                posthogReset()
                clearSentryUser()
            }

            await SessionService.checkAuthentication()

            if (isLogoutFromAnotherTab) {
                SessionService.clearCrossTabLogout()
            }
        }
    }

    // Persistent listener: EXT OAuth completes → AUTH_PUSH; EXT popup logout → AUTH_REVOKED.
    // Never treat an empty AUTH_PUSH as logout (that broke WEB after first EXT install).
    const handleExtensionMessage = async (event) => {
        if (event.source !== window) return
        const {
            data,
        } = event
        if (!data) return

        if (data.type === LODGR_EXT_AUTH_PUSH) {
            const extensionToken = data.token
            if (!extensionToken) return
            const currentToken = await sessionStore.getAccessToken()
            if (!currentToken) {
                await sessionStore.setAccessToken(extensionToken)
                await SessionService.checkAuthentication()
                // PostHog identify is fired by the centralized createEffect in main.jsx
                // when checkAuthentication flips the auth signal.
            }
            return
        }

        if (data.type === LODGR_EXT_AUTH_REVOKED) {
            const currentToken = await sessionStore.getAccessToken()
            if (!currentToken) return
            await SessionService.delete()
        }
    }

    window.addEventListener('storage', handleStorageChange)
    window.addEventListener('message', handleExtensionMessage)

    runWithOwner(owner, () => {
        onCleanup(() => {
            window.removeEventListener('storage', handleStorageChange)
            window.removeEventListener('message', handleExtensionMessage)
        })
    })
}

export const useAuthGuard = async () => {
    const pathname = window.location.pathname
    const search = window.location.search
    return new Promise(async (resolve) => {
        await SessionService.checkAuthentication()
        const authenticated = SessionService.isAuthenticated()
        if (!authenticated) {
            window.location.replace(buildSignInRedirectPath({
                from: pathname + search,
            }))
            return
        }

        resolve()

        // TENANT-5504: a session the API rejected is prompted through
        // `SessionService.signInPromptRequest`, which redirects here on a guarded
        // page itself (carrying any booking intent) — this effect must not race it.
        SessionService.markGuardedRoute()
        createEffect(() => {
            if (!SessionService.isAuthenticated() && !SessionService.isLoggingOut) {
                if (SessionService.isRejectedSessionSignOut) return
                if (SessionService.isCrossTabLogout) {
                    console.log('Logged out from another tab. Redirecting to home...')
                    window.location.replace('/')
                } else {
                    console.log('Session lost. Redirecting to login...')
                    window.location.replace(buildSignInRedirectPath({
                        from: pathname + search,
                    }))
                }
            }
        })
    })
}

/**
 * Role gate for admin-only pages. Composes `useAuthGuard` (token check +
 * cross-tab logout effect) with a lazy `account_role` fetch from `GET /user`.
 *
 * Behavior:
 *   1. Awaits `useAuthGuard()` — redirects to `/?login=true` when there's no
 *      token at all.
 *   2. Reads `account_role` from sessionStore. On a fresh login the cache is
 *      empty, so we fetch `/user` and write the role into sessionStore. Every
 *      subsequent admin-page entry within the same session reads from cache.
 *   3. Non-admin users are redirected to `/`. The fetch is fail-closed: any
 *      network/parse error treats the user as non-admin.
 *
 * The SPA-side cache is a UI gate only. The API is the authoritative role
 * boundary via `fastify.requireRole('admin')` — admin endpoints return 403 for
 * non-admins regardless of what's in localStorage. See plan §1.7b + X4.
 */
export const useAdminGuard = async () => {
    await useAuthGuard()

    let role = await sessionStore.getRole()
    if (role === undefined || role === null) {
        try {
            const user = await UserService.show()
            role = user?.account_role ?? 'user'
            await sessionStore.setRole(role)
        } catch (error) {
            captureError(error)
            role = 'user'
        }
    }

    if (role !== 'admin') {
        window.location.replace('/')
    }
}

export const useVerificationGuard = async () => {
    // TENANT-5440 QA fix: flag the acceptance status UNKNOWN synchronously,
    // BEFORE the async `GET /user` below — `main.jsx` renders a full-viewport
    // blocking scrim while the store is pending, so a freshly-loaded guarded
    // page (e.g. /account or /inbox right after Google-account creation)
    // cannot be interacted with before the consent decision is known. No-op
    // after the first resolution this page-load — client-side navigations
    // trust the in-memory store value and never flash the scrim.
    legalAcceptanceStore.beginStatusCheck()
    try {


        // Ensure the user is loaded into the store
        const userShowData = await UserService.show()
        // Grab core profile information
        const InboxService = await loadInboxService()
        const userCoreData = await InboxService.getCoreProfile()
        const mergedUserData = {
            ...userShowData,
            ...userCoreData,
        }
        if (userShowData) {
            await userStore.setUser(mergedUserData)
        } else {
            throw new Error('Failed to fetch user details.')
        }

        // Check verification status
        const isVerified = await userStore.getVerifiedAt()
        if (!isVerified) {
            window.location.replace('/?login=true')
            return
        }

        // check if profile is complete
        const ProfileService = await loadProfileService()
        const profile = await ProfileService.show()
        if (!profile.first_name || !profile.last_name) {
            const searchAndHash = `${window.location.search}${window.location.hash}`
            window.location.replace(`${ROUTES.COMPLETE_PROFILE}${searchAndHash}`)
            return
        }

        // TENANT-5440: ToS & Privacy Policy consent gate. GET /user always
        // carries `legal_acceptance` ({ accepted, is_current, ... }) per
        // spec.yaml; when the acceptance on record is stale — or the field
        // is missing, which is an API contract error and FAILS CLOSED (repo
        // non-negotiable: no silent fallbacks; a duplicate accept is
        // harmless, append-only audit table) — flag the global consent modal
        // (rendered once in main.jsx's App chrome) over the page.
        // Fire-and-forget: the modal is non-dismissible, so the page stays
        // blocked in place until accept → POST /user/legal-acceptance →
        // `UserService.recordLegalAcceptance` patches the cached user and
        // `markAccepted()` hides the modal — no refetch, no redirect needed.
        const legalAcceptance = mergedUserData?.legal_acceptance
        if (!legalAcceptance) {
            captureError(new Error('useVerificationGuard: GET /user carried no legal_acceptance — failing closed to the consent gate'))
        }
        if (legalAcceptance?.is_current !== true) {
            legalAcceptanceStore.require()
        }
        // Status now KNOWN — drop the blocking scrim. In the stale branch the
        // require() above flipped the modal on in the same synchronous tick,
        // so the page is never interactive between scrim and modal. Note the
        // two hard-redirect early-returns above (unverified → login,
        // incomplete profile → /complete-profile) deliberately leave the
        // scrim up: `window.location.replace` is tearing the page down and it
        // must not flash interactive meanwhile.
        legalAcceptanceStore.resolveStatusCheck()

        return {
            profile,
            user: mergedUserData,
        }
    } catch (error) {
        // Status check failed — drop the scrim WITHOUT marking the status
        // resolved (next guarded navigation re-checks). 401/403/404 tears the
        // session down and redirects to login anyway; other errors keep the
        // pre-existing captureError surface, and pinning an eternal scrim
        // over a page whose data fetch already failed would only trade one
        // broken state for a worse one.
        legalAcceptanceStore.abortStatusCheck()
        if ([
            401,403,404,
        ].includes(error?.statusCode)) {
            await SessionService.delete()
        } else {
            console.error('useVerificationGuard - Error:', error)
            captureError(error) // so it can be captured by Sentry

        }
    }
}
export const checkPropertyOwnership = async (propertyId) => {
    try {
        const [
            ProfileService,
            PropertyService,
        ] = await Promise.all([
            loadProfileService(),
            loadPropertyService(),
        ])
        const profile = await ProfileService.show()
        const property = await PropertyService.show(propertyId)
        return property.profile_id === profile.profile_id
    } catch (error) {
        console.error('checkPropertyOwnership - Error:', error)
        captureError(error)
        return false
    }
}

export const usePropertyGuard = async (propertyId) => {
    const isOwner = await checkPropertyOwnership(propertyId)
    console.log('isOwner', isOwner)
    if (!isOwner) {
        window.location.replace('/properties')
        return false
    }

    return true
}

export const appendSearchParams = (link, extraParameters = {}) => {
    const url = new URL(link, window.location.origin)

    // Merge existing window.location.search params
    const currentParameters = new URLSearchParams(window.location.search)
    for (const [
        key,
        value,
    ] of currentParameters.entries()) {
        url.searchParams.set(key, value)
    }

    // Append or override with extraParams
    for (const [
        key,
        value,
    ] of Object.entries(extraParameters)) {
        url.searchParams.set(key, value)
    }

    const queryString = url.searchParams.toString()
    return queryString ? `${url.pathname}?${queryString}` : url.pathname
}

export const authTokenRedirect = (redirectUrl, authToken) => {
    if (!redirectUrl || !authToken) return

    const formattedUrl = redirectUrl.startsWith('http') ? redirectUrl : `https://${redirectUrl}`
    // Append auth token as a query param
    const url = new URL(formattedUrl)
    url.searchParams.set('auth_token', authToken)

    window.location = url
}


export const qsCommaArrays = {

    /**
     * Parses a query string (or an already-parsed key→value(s) object, e.g.
     * from solid-router's `useSearchParams()`) into an object, splitting
     * comma-separated values into arrays and coercing all top-level values
     * into arrays for consistency.
     *
     * Example:
     *   "tags=alpha,beta&type=demo"
     *   → { tags: ['alpha', 'beta'], type: ['demo'] }
     *
     * Native replacement for the `qs` package (+ its ~50KB polyfill chain of
     * transitive deps), which was used for exactly this one comma-array
     * feature app-wide. Only two call shapes are used anywhere in this repo:
     * a raw query string, and solid-router's already-parsed params object —
     * both are handled below. See TENANT-5036 bundle audit.
     */
    parse (input) {
        const raw = {}
        if (typeof input === 'string') {
            const withoutPrefix = input.startsWith('?') ? input.slice(1) : input
            const parameters = new URLSearchParams(withoutPrefix)
            for (const key of parameters.keys()) {
                if (key in raw) continue // getAll() below already covers every value for this key
                raw[key] = parameters.getAll(key).join(',')
            }
        } else if (input && typeof input === 'object') {
            Object.assign(raw, input)
        }

        const parsed = {}
        for (const [
            key,
            value,
        ] of Object.entries(raw)) {
            const values = Array.isArray(value) ? value : [value]
            parsed[key] = values.flatMap((entry) => String(entry).split(','))
        }

        return parsed
    },
    /**
     * Converts an object into a query string using comma-separated arrays.
     * Keeps commas between array elements unencoded so round-trip with parse works.
     * Example:
     *   { nextSections: ['CONNECT_PROPERTY', 'SETUP_EXTERNAL'] } → "nextSections=CONNECT_PROPERTY,SETUP_EXTERNAL"
     */
    stringify (object) {
        const parts = []
        for (const [
            key,
            value,
        ] of Object.entries(object)) {
            if (Array.isArray(value)) {
                if (value.length === 0) continue // qs omits keys with an empty array entirely
                parts.push(`${key}=${value.map((entry) => encodeURIComponent(entry)).join(',')}`)
            } else if (value !== undefined && value !== null) {
                parts.push(`${key}=${encodeURIComponent(value)}`)
            }
        }
        return parts.join('&')
    },
}
